"use client";

import React, { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { ZodSchema } from "zod";

import { WizardStepper } from "./WizardStepper";

import { FormHandle } from "../Form/types";
import { StepStatus, WizardContextValue, WizardProps, WizardStepConfig } from "./types";

import { cn } from "../../../lib/utils";

import { WizardContext } from "./context";

/**
 * Multi-step wizard with built-in stepper, form validation, and navigation controls.
 */
export const Wizard = ({
	id,
	title,
	children,
	onComplete,
	defaultStep = 0,
	stepperClassName,
	dangerouslySetClassName,
	onStepChange,
	onExit,
	initialData,
	submitLabel,
}: WizardProps) => {
	const [currentStep, setCurrentStep] = useState(defaultStep);
	const [wizardData, setWizardData] = useState<Record<string, unknown>>(initialData ?? {});
	const [completedSteps, setCompletedSteps] = useState<number[]>([]);
	const [visitedSteps, setVisitedSteps] = useState<number[]>([defaultStep]);
	const [stepConfigs, setStepConfigs] = useState<WizardStepConfig[]>([]);
	const [stepsWithErrors, setStepsWithErrors] = useState<number[]>([]);
	const [isNextDisabled, setNextDisabled] = useState(false);
	const stepFormRefs = useRef<Map<string, FormHandle<ZodSchema> | null>>(new Map());

	const totalSteps = useMemo(() => React.Children.count(children), [children]);

	const onStepChangeEvent = useEffectEvent(() => {
		onStepChange?.(currentStep);
	});

	useEffect(() => {
		onStepChangeEvent();
		setVisitedSteps((prev) => {
			if (prev.includes(currentStep)) return prev;
			return [...prev, currentStep];
		});
	}, [currentStep]);

	const registerStep = useCallback((config: WizardStepConfig) => {
		setStepConfigs((prev) => {
			const existingIndex = prev.findIndex((s) => s.id === config.id);
			if (existingIndex === -1) return [...prev, config];
			const existing = prev[existingIndex];
			if (existing.beforeNext === config.beforeNext) return prev;
			const updated = [...prev];
			updated[existingIndex] = { ...existing, ...config };
			return updated;
		});
	}, []);

	const setStepData = useCallback(
		(stepId: string, data: unknown) => {
			setWizardData((prev) => ({ ...prev, [stepId]: data }));
		},
		[setWizardData]
	);

	const setStepFormRef = useCallback((stepId: string, ref: FormHandle<ZodSchema> | null) => {
		stepFormRefs.current.set(stepId, ref);
	}, []);

	const getCurrentFormRef = useCallback((): FormHandle<ZodSchema> | null => {
		const config = stepConfigs[currentStep];
		if (!config) return null;
		return stepFormRefs.current.get(config.id) || null;
	}, [currentStep, stepConfigs]);

	const isStepComplete = useCallback(
		(stepIndex: number): boolean => completedSteps.includes(stepIndex),
		[completedSteps]
	);

	const markStepComplete = useCallback((stepIndex: number) => {
		setCompletedSteps((prev) => {
			if (prev.includes(stepIndex)) return prev;
			return [...prev, stepIndex];
		});
	}, []);

	const markStepError = useCallback((stepIndex: number) => {
		setStepsWithErrors((prev) => {
			if (prev.includes(stepIndex)) return prev;
			return [...prev, stepIndex];
		});
	}, []);

	const clearStepError = useCallback((stepIndex: number) => {
		setStepsWithErrors((prev) => prev.filter((i) => i !== stepIndex));
	}, []);

	const getStepStatus = useCallback(
		(stepIndex: number): StepStatus => {
			if (stepsWithErrors.includes(stepIndex)) return "error";
			if (stepIndex === currentStep) return "active";
			if (completedSteps.includes(stepIndex)) return "completed";
			return "upcoming";
		},
		[stepsWithErrors, currentStep, completedSteps]
	);

	const canNavigateToStep = useCallback(
		(step: number): boolean => {
			if (step < 0 || step >= totalSteps) return false;
			return visitedSteps.includes(step) || completedSteps.includes(step);
		},
		[totalSteps, completedSteps, visitedSteps]
	);

	const saveCurrentStepData = useCallback(() => {
		const config = stepConfigs[currentStep];
		if (!config) return;
		const formRef = getCurrentFormRef();
		if (!formRef) return;
		try {
			const currentValues = formRef.getFormValues();
			setStepData(config.id, currentValues);
		} catch {
			// silently continue
		}
	}, [currentStep, stepConfigs, getCurrentFormRef, setStepData]);

	const validateCurrentStep = useCallback(async (): Promise<boolean> => {
		const config = stepConfigs[currentStep];
		const formRef = getCurrentFormRef();

		if (config?.schema && formRef) {
			try {
				const isValid = await formRef.validate(true);
				if (!isValid) {
					markStepError(currentStep);
					return false;
				}
				clearStepError(currentStep);
			} catch {
				markStepError(currentStep);
				return false;
			}
		}

		if (config?.beforeNext) {
			const stepData = wizardData[config.id];
			const canProceed = await config.beforeNext(stepData);
			if (!canProceed) {
				markStepError(currentStep);
				return false;
			}
		}

		clearStepError(currentStep);
		return true;
	}, [currentStep, stepConfigs, getCurrentFormRef, markStepError, clearStepError, wizardData]);

	const goToStep = useCallback(
		async (step: number) => {
			if (!canNavigateToStep(step) && step !== currentStep) return;

			const isNavigatingForward = step > currentStep;
			if (isNavigatingForward || completedSteps.includes(currentStep)) {
				if (!(await validateCurrentStep())) return;
			}

			saveCurrentStepData();
			setCurrentStep(step);
		},
		[canNavigateToStep, currentStep, completedSteps, validateCurrentStep, saveCurrentStepData]
	);

	const performNavigation = useCallback(
		async (currentStepData?: { stepId: string; data: unknown }) => {
			const config = stepConfigs[currentStep];
			if (!config) return;

			const completeWizardData = currentStepData
				? { ...wizardData, [currentStepData.stepId]: currentStepData.data }
				: wizardData;

			if (config.beforeNext) {
				const canProceed = await config.beforeNext(completeWizardData[config.id]);
				if (!canProceed) {
					markStepError(currentStep);
					return;
				}
			}

			markStepComplete(currentStep);
			clearStepError(currentStep);

			if (currentStep === totalSteps - 1) {
				await onComplete?.(completeWizardData);
				return;
			}

			setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
		},
		[currentStep, stepConfigs, totalSteps, wizardData, onComplete, markStepComplete, markStepError, clearStepError]
	);

	const goToNext = useCallback(async () => {
		const config = stepConfigs[currentStep];
		const formRef = getCurrentFormRef();

		if (config?.schema && formRef) {
			try {
				const isValid = await formRef.validate(true);
				if (!isValid) {
					markStepError(currentStep);
					return;
				}
				clearStepError(currentStep);
				await formRef.submit();
				return;
			} catch {
				markStepError(currentStep);
				return;
			}
		}

		clearStepError(currentStep);
		await performNavigation();
	}, [currentStep, stepConfigs, getCurrentFormRef, performNavigation, markStepError, clearStepError]);

	const goToPrevious = useCallback(async () => {
		if (completedSteps.includes(currentStep)) {
			if (!(await validateCurrentStep())) return;
		}

		saveCurrentStepData();
		setCurrentStep((prev) => Math.max(prev - 1, 0));
	}, [completedSteps, currentStep, validateCurrentStep, saveCurrentStepData]);

	const handleStepperClick = useCallback(
		async (stepIndex: number) => {
			if (stepIndex === currentStep) return;
			if (!visitedSteps.includes(stepIndex) && !completedSteps.includes(stepIndex)) return;

			const isNavigatingForward = stepIndex > currentStep;
			if (isNavigatingForward || completedSteps.includes(currentStep)) {
				if (!(await validateCurrentStep())) return;
			}

			saveCurrentStepData();
			setCurrentStep(stepIndex);
		},
		[currentStep, completedSteps, visitedSteps, validateCurrentStep, saveCurrentStepData]
	);

	const isFirstStep = currentStep === 0;
	const isLastStep = currentStep === totalSteps - 1;

	const steps = useMemo(() => {
		const childArray = React.Children.toArray(children);
		return childArray.map((child, index) => {
			if (React.isValidElement(child)) {
				const props = child.props as { id?: string; title?: string };
				return {
					id: props.id || `step-${index}`,
					title: props.title || `Step ${index + 1}`,
				};
			}
			return { id: "", title: "" };
		});
	}, [children]);

	const contextValue: WizardContextValue = useMemo(
		() => ({
			currentStep,
			totalSteps,
			wizardData,
			isFirstStep,
			isLastStep,
			goToStep,
			goToNext,
			goToPrevious,
			setStepData,
			canNavigateToStep,
			registerStep,
			getCurrentFormRef,
			setStepFormRef,
			wizardId: id,
			markStepComplete,
			isStepComplete,
			completedSteps,
			visitedSteps,
			stepsWithErrors,
			steps,
			performNavigation,
			markStepError,
			clearStepError,
			getStepStatus,
			handleStepperClick,
			onExit,
			submitLabel,
			isNextDisabled,
			setNextDisabled,
		}),
		[
			currentStep,
			totalSteps,
			wizardData,
			isFirstStep,
			isLastStep,
			goToStep,
			goToNext,
			goToPrevious,
			setStepData,
			canNavigateToStep,
			registerStep,
			getCurrentFormRef,
			setStepFormRef,
			id,
			markStepComplete,
			isStepComplete,
			completedSteps,
			visitedSteps,
			stepsWithErrors,
			steps,
			performNavigation,
			markStepError,
			clearStepError,
			getStepStatus,
			handleStepperClick,
			onExit,
			submitLabel,
			isNextDisabled,
			setNextDisabled,
		]
	);

	const childArray = React.Children.toArray(children);
	const currentStepElement = childArray[currentStep];

	return (
		<WizardContext.Provider value={contextValue}>
			<div data-wizard-id={id} className={cn("flex h-full gap-6 bg-neutral-0", dangerouslySetClassName)}>
				<WizardStepper title={title} dangerouslySetClassName={stepperClassName} onExit={onExit} />
				<div className="flex-1 pt-6">{currentStepElement}</div>
			</div>
		</WizardContext.Provider>
	);
};

Wizard.displayName = "Wizard";
